import React, { useState, useRef, useEffect } from 'react';
import CalmiCharacter from '../pet/components/CalmiCharacter';
import { Message } from '../../shared/types';
import { Button, NavigationBar, Card, ThemedScreen } from '../../shared/components';
import { aiService, chatService } from '../../shared/services';
import { notificationService } from '../../shared/services/notificationService';
import { Chat } from '../../shared/types';

const ChatScreen: React.FC<{ userName: string; onNavigate: (screen: string) => void }> = ({ userName, onNavigate }) => {
  const [chat, setChat] = useState<Chat | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Preguntas iniciales aleatorias para iniciar conversación
  const openingQuestions = [
    `¡Hola ${userName}! ¿Cómo estuvo tu día hoy? 🌟`,
    `¡Hola ${userName}! ¿Qué te hizo sonreír hoy? 😊`,
    `¡Hola ${userName}! ¿Hay algo especial que quieras contarme? 💭`,
    `¡Hola ${userName}! ¿Cómo te sientes en este momento? 🌈`,
    `¡Hola ${userName}! ¿Qué hiciste divertido hoy? 🎮`,
    `¡Hola ${userName}! ¿Tienes algún sueño que quieras compartir? ⭐`,
    `¡Hola ${userName}! ¿Qué te gusta hacer cuando estás feliz? 🎨`,
    `¡Hola ${userName}! ¿Hay algo que te preocupe hoy? 🤗`
  ];

  const getRandomOpeningQuestion = () => {
    const childProfile = localStorage.getItem('calmi_child_profile');
    if (childProfile) {
      const parsed = JSON.parse(childProfile);
      const childName = parsed.name || userName;
      const questions = [
        `¡Hola ${childName}! ¿Cómo estuvo tu día hoy? 🌟`,
        `¡Hola ${childName}! ¿Qué te hizo sonreír hoy? 😊`,
        `¡Hola ${childName}! ¿Hay algo especial que quieras contarme? 💭`,
        `¡Hola ${childName}! ¿Cómo te sientes en este momento? 🌈`,
        `¡Hola ${childName}! ¿Qué hiciste divertido hoy? 🎮`,
        `¡Hola ${childName}! ¿Tienes algún sueño que quieras compartir? ⭐`,
        `¡Hola ${childName}! ¿Qué te gusta hacer cuando estás feliz? 🎨`,
        `¡Hola ${childName}! ¿Hay algo que te preocupe hoy? 🤗`
      ];
      return questions[Math.floor(Math.random() * questions.length)];
    }
    return openingQuestions[Math.floor(Math.random() * openingQuestions.length)];
  };

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let current = chatService.getCurrentChatId();
    let currentChat: Chat;
    if (!current) {
      currentChat = chatService.createChat();
    } else {
      const existing = chatService.getChat(current);
      currentChat = existing || chatService.createChat();
    }
    if (currentChat.messages.length === 0) {
      const openingMessage: Message = {
        id: '1',
        text: getRandomOpeningQuestion(),
        sender: 'calmi',
        timestamp: new Date()
      };
      currentChat.messages = [openingMessage];
      chatService.updateChat(currentChat);
    }
    setChat(currentChat);
    setMessages(currentChat.messages);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (chat && messages.length > 0) {
      const updated = { ...chat, messages };
      chatService.updateChat(updated);
      setChat(updated);
    }
  }, [messages, chat]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inputMessage.trim() && chat) {
      const chatId = chat.id;
      const userMessage: Message = {
        id: Date.now().toString(),
        text: inputMessage.trim(),
        sender: 'user',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      chatService.addMessage(chatId, userMessage);
      setInputMessage('');
      setIsTyping(true);

      // Obtener respuesta de IA
      const conversationHistory = messages.map(msg => ({
        role: msg.sender === 'user' ? 'user' as const : 'assistant' as const,
        content: msg.text
      }));

      // Analizar riesgo y emoción del mensaje del menor
      const analysis = aiService.analyzeMessage(userMessage.text);
      const childProfile = localStorage.getItem('calmi_child_profile');
      const childName = childProfile ? JSON.parse(childProfile).name : userName;

      if (analysis.risk !== 'none') {
        const childId = localStorage.getItem('calmi_child_id') || childName;
        notificationService.createAlert({
          childId,
          childName,
          type: analysis.alertType || 'emotional_concern',
          severity: analysis.risk,
          message: `${childName} envió un mensaje que indica una situación de nivel ${analysis.risk}. Emoción detectada: ${analysis.emotion}. Palabras clave: ${analysis.keywords.join(', ')}. Mensaje: "${userMessage.text}"`,
          date: new Date()
        });
      }

      const response = await aiService.generateResponse(userMessage.text, conversationHistory);
      
      const calmiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: response.response,
        sender: 'calmi',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, calmiResponse]);
      chatService.addMessage(chatId, calmiResponse);
      setIsTyping(false);
    }
  };

  return (
    <ThemedScreen className="p-4 sm:p-6 md:p-8 pb-24">
      <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => onNavigate('home')} 
            className="p-3 sm:p-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all btn-interactive border border-slate-200"
          >
            <span className="text-xl sm:text-2xl">←</span>
          </button>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Desahógate con Calmi 💭</h1>
          <button
            onClick={() => onNavigate('chat-list')}
            className="p-3 sm:p-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all btn-interactive border border-slate-200"
            title="Mis chats"
          >
            <span className="text-xl sm:text-2xl">📂</span>
          </button>
        </div>

        {/* Description */}
        <p className="text-sm sm:text-base text-slate-600 text-center mb-6">
          Cuéntale a Calmi cómo te sientes, siempre te escuchará.
        </p>

        {/* Chat Messages */}
        <Card className="mb-6 h-64 sm:h-80 md:h-96 overflow-y-auto p-4 sm:p-6 bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg chat-container">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} mb-3 animate-bounce-in`}
            >
              <div
                className={`max-w-xs sm:max-w-sm md:max-w-md px-4 py-3 rounded-2xl ${
                  message.sender === 'user'
                    ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-white rounded-br-none shadow-md'
                    : 'bg-slate-100 text-slate-800 rounded-bl-none shadow-sm'
                }`}
              >
                <p className="text-sm sm:text-base leading-relaxed">{message.text}</p>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start mb-3">
              <div className="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-none px-4 py-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </Card>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="flex gap-2 mb-6">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Escribe lo que sientes..."
            className="flex-1 px-4 py-2 sm:py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
          />
          <Button 
            type="submit" 
            size="md"
            className="btn-interactive bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 shadow-md hover:shadow-lg transition-all"
          >
            <span className="text-lg">➤</span>
          </Button>
        </form>

        {/* Bottom Navigation */}
        <NavigationBar activeItem="chat" onNavigate={onNavigate} />
      </div>
    </ThemedScreen>
  );
};

export default ChatScreen;
